/**
 * "Risk ratings explained" — concept explainer. Pure modal-style steps with
 * no DOM attachment, since the tour explains the meaning rather than pointing
 * to specific UI. Works regardless of the page the user is on.
 *
 * The risk pills shown inside each tooltip use inline styles that match the
 * `.risk_badge` styles in ClientList.vue (which are scoped, hence inlined
 * here). If those visuals change there, update them here too.
 */

import { NEXT, BACK, START, SKIP, DONE } from './_helpers.js';

// Inline-styled risk pill that visually matches the badge users see in the
// client list, client detail page, and dashboard. Keep in sync with the
// `.risk_badge` / `.risk_<level>` rules in ClientList.vue.
const PILL_BASE = 'display:inline-block;font-size:11px;font-weight:700;padding:2px 10px;border-radius:8px;letter-spacing:0.3px;';
const pill = (level) => {
    const colours = {
        Low:    'background:#dcfce7;color:#166534;',
        Medium: 'background:#fef3c7;color:#92400e;',
        High:   'background:#fee2e2;color:#991b1b;',
    };
    return `<span style="${PILL_BASE}${colours[level]}">${level}</span>`;
};

const allPills = `<span style="display:inline-flex;gap:6px;align-items:center;">
    ${pill('Low')} ${pill('Medium')} ${pill('High')}
</span>`;

export const riskRatingsTour = {
    id: 'risk-ratings',
    title: 'Risk ratings explained',
    description: 'What "Low", "Medium", and "High" risk mean for your clients — how each rating is calculated and what it triggers downstream.',
    autoLaunchOn: { page: 'clients' },

    steps: [
        {
            id: 'intro',
            title: 'Client risk ratings',
            text: `Every client onboarded into the platform is assigned a risk rating —
                one of three levels:
                <div style="margin:10px 0;text-align:center;">${allPills}</div>
                The rating drives how often you must review the client, how strictly you
                must verify identity, and what extra checks must be performed before any
                designated service is provided.
                <br><br>
                You'll see these pills in the client list, on each client's detail page,
                and on the dashboard.`,
            buttons: [START],
        },
        {
            id: 'low',
            title: 'Low risk',
            text: `<div style="margin-bottom:8px;">${pill('Low')}</div>
                <strong>Simplified CDD applies.</strong>
                <ul style="margin:6px 0 0 18px;padding:0;">
                    <li>Verify identity via certified-copy inspection of a single
                        government-issued ID.</li>
                    <li>Associated persons are usually optional (unless a third party is
                        instructing or providing funds).</li>
                    <li>Longer review cycle.</li>
                </ul>
                Typical for individual clients with no PEP/sanctions exposure and a
                straightforward source of funds.`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'medium',
            title: 'Medium risk',
            text: `<div style="margin-bottom:8px;">${pill('Medium')}</div>
                <strong>Standard CDD applies.</strong>
                <ul style="margin:6px 0 0 18px;padding:0;">
                    <li>Full identity verification — certified copies, with electronic
                        verification optional but recommended.</li>
                    <li>All associated persons (officers, beneficial owners, trustees)
                        must be on file.</li>
                    <li>Standard review cycle.</li>
                </ul>
                Typical for body-corporate clients, trusts, and individuals with some
                complexity in their structure or funding.`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'high',
            title: 'High risk',
            text: `<div style="margin-bottom:8px;">${pill('High')}</div>
                <strong>Enhanced CDD is mandatory.</strong>
                <ul style="margin:6px 0 0 18px;padding:0;">
                    <li>Electronic identity verification.</li>
                    <li>PEP and sanctions screening at onboarding and on every review.</li>
                    <li>Adverse media check.</li>
                    <li>Documented Source of Funds and Source of Wealth.</li>
                    <li><strong>Senior Manager approval is required before any
                        designated service can be provided.</strong></li>
                    <li>Shorter review cycle, and ongoing monitoring is more frequent.</li>
                </ul>
                Triggered by PEP exposure, high-risk jurisdiction connections, sanctions
                hits, or red flags in the risk-assessment questions.`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'how-its-set',
            title: 'How the rating is calculated',
            text: `When you onboard a client manually, <strong>Step 3 of the wizard</strong>
                asks a series of risk questions — country of operation, nature of
                services, PEP/sanctions exposure, and so on. The answers feed into a
                weighted calculation that produces the rating automatically.
                <br><br>
                You can override the rating in special cases, but the override must be
                justified in writing and approved by a Senior Manager.`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'where-it-shows',
            title: 'Where you\'ll see it',
            text: `The risk badge appears in lots of places:
                <ul style="margin:6px 0 0 18px;padding:0;">
                    <li>The <strong>Client Risk Summary</strong> on the dashboard
                        (totals by rating, plus any High-risk holds).</li>
                    <li>The <strong>Clients list</strong> — filter by rating, sort by it.</li>
                    <li>Inside any individual client's detail page, near the top.</li>
                    <li>On <strong>escalation cards</strong> — high-risk clients escalate
                        with a red urgency by default.</li>
                </ul>`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'finish',
            title: 'That\'s risk ratings',
            text: `If a client's situation changes (new PEP exposure, loss of beneficial
                owner info, jurisdictional change), re-run the risk assessment and the
                rating will update — and the system will surface any new requirements
                (e.g. SM approval, Enhanced CDD steps) on the client's page.`,
            buttons: [BACK, DONE],
        },
    ],
};
